import React, { useEffect, useMemo, useState } from 'react';
import { Download, Eye, FileText, Printer, Search } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const isoDay = date => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const presetRange = preset => {
  const today = new Date();
  if (preset === 'today') return [isoDay(today), isoDay(today)];
  if (preset === '7d') {
    const from = new Date(today); from.setDate(from.getDate() - 6);
    return [isoDay(from), isoDay(today)];
  }
  if (preset === 'month') {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    return [isoDay(from), isoDay(today)];
  }
  return [isoDay(today), isoDay(today)];
};

export default function HaccpReportsPanel({ initialLotId = '', onLotChange }) {
  const [lots, setLots] = useState([]);
  const [query, setQuery] = useState('');
  const [lotId, setLotId] = useState(initialLotId || '');
  const [preset, setPreset] = useState('7d');
  const initialRange = useMemo(() => presetRange('7d'), []);
  const [dateFrom, setDateFrom] = useState(initialRange[0]);
  const [dateTo, setDateTo] = useState(initialRange[1]);
  const [previewState, setPreviewState] = useState(null);
  const [working, setWorking] = useState('');

  useEffect(() => { fetch(`${BACKEND_URL}/api/lots/`, { credentials: 'include' }).then(r => r.ok ? r.json() : []).then(setLots).catch(() => {}); }, []);
  useEffect(() => { if (initialLotId) setLotId(initialLotId); }, [initialLotId]);
  useEffect(() => () => { if (previewState?.url) URL.revokeObjectURL(previewState.url); }, [previewState]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase(); if (!q) return [];
    return lots.filter(l => [l.lot_number, l.commercial_name, l.ingredient_name, l.product_code].some(v => String(v || '').toLowerCase().includes(q))).slice(0, 8);
  }, [lots, query]);
  const selected = lots.find(l => l.lot_id === lotId);
  const selectLot = lot => { setLotId(lot.lot_id); onLotChange?.(lot.lot_id); setQuery(`${lot.lot_number} · ${lot.commercial_name || lot.ingredient_name || 'Prodotto'}`); };

  const applyPreset = value => {
    setPreset(value);
    if (value !== 'custom') {
      const [from, to] = presetRange(value);
      setDateFrom(from); setDateTo(to);
    }
  };

  const periodQuery = () => new URLSearchParams({ from: dateFrom, to: dateTo }).toString();
  const endpointFor = kind => {
    if (kind === 'register') return `/api/reports/register.pdf?${periodQuery()}`;
    if (kind === 'nonconformities') return `/api/reports/nonconformities.pdf?${periodQuery()}`;
    if (!lotId) throw new Error('Seleziona prima un lotto');
    return `/api/reports/traceability/${encodeURIComponent(lotId)}.pdf`;
  };
  const metaFor = kind => kind === 'register'
    ? { title: 'Registro HACCP', filename: `registro_haccp_${dateFrom}_${dateTo}.pdf` }
    : kind === 'nonconformities'
      ? { title: 'Non conformità HACCP', filename: `non_conformita_${dateFrom}_${dateTo}.pdf` }
      : { title: 'Report di rintracciabilità', filename: `rintracciabilita_${selected?.lot_number || 'lotto'}.pdf` };

  const getPdf = async kind => {
    const r = await fetch(`${BACKEND_URL}${endpointFor(kind)}`, { credentials: 'include' });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Impossibile generare il PDF'); }
    return r.blob();
  };
  const preview = async kind => {
    setWorking(`preview:${kind}`);
    try {
      const blob = await getPdf(kind);
      if (previewState?.url) URL.revokeObjectURL(previewState.url);
      setPreviewState({ url: URL.createObjectURL(blob), ...metaFor(kind) });
    } catch (e) { toast.error(e.message); } finally { setWorking(''); }
  };
  const download = async kind => {
    setWorking(`download:${kind}`);
    try {
      const blob = await getPdf(kind); const url = URL.createObjectURL(blob); const a = document.createElement('a');
      a.href = url; a.download = metaFor(kind).filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('PDF generato');
    } catch (e) { toast.error(e.message); } finally { setWorking(''); }
  };
  const printReport = async kind => {
    setWorking(`print:${kind}`);
    try {
      const blob = await getPdf(kind); const url = URL.createObjectURL(blob); const iframe = document.createElement('iframe');
      iframe.title = 'Stampa report'; iframe.style.position = 'fixed'; iframe.style.width = '1px'; iframe.style.height = '1px'; iframe.style.opacity = '0'; iframe.style.pointerEvents = 'none';
      const cleanup = () => { try { iframe.remove(); } catch {} URL.revokeObjectURL(url); };
      iframe.onload = () => {
        try {
          const win = iframe.contentWindow; if (!win) throw new Error('Dialog di stampa non disponibile');
          win.addEventListener?.('afterprint', cleanup, { once: true }); win.focus(); win.print(); window.setTimeout(cleanup, 60000);
        } catch (error) { cleanup(); toast.error(error.message || 'Impossibile aprire la stampa'); }
      };
      iframe.src = url; document.body.appendChild(iframe);
    } catch (e) { toast.error(e.message); } finally { setWorking(''); }
  };

  const Actions = ({ kind, disabled = false }) => <div className="flex flex-wrap gap-2">
    <Button variant="outline" onClick={() => preview(kind)} disabled={disabled || !!working}><Eye/>Anteprima</Button>
    <Button variant="outline" onClick={() => download(kind)} disabled={disabled || !!working}><Download/>Scarica PDF</Button>
    <Button onClick={() => printReport(kind)} disabled={disabled || !!working}><Printer/>Stampa</Button>
  </div>;

  return <div className="space-y-4">
    <div className="surface p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><FileText className="w-5 h-5 text-primary"/></div>
        <div><h3 className="font-heading font-medium">Periodo report HACCP</h3><p className="text-sm text-muted-foreground mt-1">Il periodo si applica al Registro HACCP e al report Non conformità.</p></div>
      </div>
      <div className="grid gap-3 mt-4 sm:grid-cols-[180px_1fr_1fr]">
        <div><Label>Periodo rapido</Label><Select value={preset} onValueChange={applyPreset}><SelectTrigger className="mt-1"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="today">Oggi</SelectItem><SelectItem value="7d">Ultimi 7 giorni</SelectItem><SelectItem value="month">Mese corrente</SelectItem><SelectItem value="custom">Personalizzato</SelectItem></SelectContent></Select></div>
        <div><Label>Dal</Label><Input className="mt-1" type="date" value={dateFrom} onChange={e => { setPreset('custom'); setDateFrom(e.target.value); }}/></div>
        <div><Label>Al</Label><Input className="mt-1" type="date" value={dateTo} onChange={e => { setPreset('custom'); setDateTo(e.target.value); }}/></div>
      </div>
    </div>

    <div className="grid gap-4 xl:grid-cols-2">
      <div className="surface p-4 sm:p-5 border-primary/30">
        <h3 className="font-heading font-medium">Registro HACCP</h3>
        <p className="text-sm text-muted-foreground mt-1">Pacchetto ispettivo del periodo: ricevimento merci, produzioni, scarti/rettifiche, non conformità e riferimenti DDT registrati.</p>
        <div className="mt-4"><Actions kind="register" disabled={!dateFrom || !dateTo || dateFrom > dateTo}/></div>
      </div>
      <div className="surface p-4 sm:p-5 border-primary/30">
        <h3 className="font-heading font-medium">Non conformità</h3>
        <p className="text-sm text-muted-foreground mt-1">Elenco delle non conformità realmente registrate al ricevimento, con lotto, fornitore, DDT e motivo.</p>
        <div className="mt-4"><Actions kind="nonconformities" disabled={!dateFrom || !dateTo || dateFrom > dateTo}/></div>
      </div>
    </div>

    <div className="surface p-4 sm:p-5 border-primary/30">
      <div className="flex items-start gap-3"><div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><FileText className="w-5 h-5 text-primary"/></div><div><h3 className="font-heading font-medium">Rintracciabilità lotto</h3><p className="text-sm text-muted-foreground mt-1">Seleziona un lotto e genera il documento A4 dalla stessa catena dati visualizzata nell’app.</p></div></div>
      <div className="relative mt-4"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"/><Input className="pl-9 min-h-11" value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca lotto, prodotto o codice articolo"/></div>
      {matches.length > 0 && <div className="border rounded-lg divide-y overflow-hidden mt-2">{matches.map(l => <button type="button" key={l.lot_id} onClick={() => selectLot(l)} className="w-full min-h-11 p-3 text-left hover:bg-slate-50"><span className="font-medium">{l.commercial_name || l.ingredient_name}</span><span className="text-xs text-muted-foreground ml-2">Lotto {l.lot_number}</span></button>)}</div>}
      {selected && <div className="surface-muted p-3 text-sm mt-3"><strong>{selected.commercial_name || selected.ingredient_name}</strong> · Lotto {selected.lot_number}</div>}
      <div className="grid gap-3 mt-3 sm:grid-cols-[minmax(220px,1fr)_auto] sm:items-end"><div><Label>Stampante</Label><Select value="system" disabled><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="system">Stampante di sistema</SelectItem></SelectContent></Select><p className="mt-1 text-[11px] text-muted-foreground">La scelta della stampante A4 avviene nel dialog del dispositivo.</p></div><Actions kind="traceability" disabled={!lotId}/></div>
    </div>

    <Dialog open={!!previewState?.url} onOpenChange={open => { if (!open && previewState?.url) { URL.revokeObjectURL(previewState.url); setPreviewState(null); } }}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col"><DialogHeader><DialogTitle>Anteprima {previewState?.title}</DialogTitle></DialogHeader>{previewState?.url && <iframe title={`Anteprima ${previewState.title}`} src={previewState.url} className="w-full flex-1 rounded-lg border"/>}</DialogContent>
    </Dialog>
  </div>;
}
